import {
    useWindowDimensions,
    ScrollView,
    Image,
    View
} from "react-native";
import type OrganizationPreviewModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BuildingIcon
} from "lucide-react-native";

const OrganizationPreviewModal = ({
    modalId,
    org
}: OrganizationPreviewModalProps) => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const InfoRow = ({
        label,
        value
    }: {
        label: string;
        value: string;
    }) => <View
        style={[
            stylesheet.infoRow,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <Text
            style={stylesheet.unselectableText}
            variant="labelMediumSize"
            color="high"
        >
            {label}
        </Text>
        <Text
            variant="bodyMediumSize"
        >
            {value || "-"}
        </Text>
    </View>;

    return <View
        style={[
            stylesheet.container,
            {
                backgroundColor: colors.content.container.default,
                width: isMobile ? "100%" : 500,
                borderRadius: radiuses.md,
                padding: spaces.spacingXl
            }
        ]}
    >
        <Text
            style={stylesheet.unselectableText}
            variant="titleMediumSize"
        >
            {localize("org-preview-title")}
        </Text>
        <View
            style={[
                stylesheet.imageContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            {
                org.profilePicturePath ?
                    <Image
                        style={[
                            stylesheet.profileImage,
                            {
                                borderRadius: radiuses.full
                            }
                        ]}
                        source={{
                            uri: org.profilePicturePath
                        }}
                    />
                :
                    <View
                        style={[
                            stylesheet.profileImage,
                            {
                                backgroundColor: colors.content.container.mid,
                                borderRadius: radiuses.full,
                                justifyContent: "center",
                                alignItems: "center"
                            }
                        ]}
                    >
                        <BuildingIcon
                            color={colors.content.icon.high}
                            size={40}
                        />
                    </View>
            }
        </View>
        <ScrollView
            style={[
                stylesheet.scroll,
                {
                    marginVertical: spaces.spacingXl,
                    flexShrink: 1
                }
            ]}
            contentContainerStyle={[
                stylesheet.scrollContent,
                {
                    paddingBottom: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
            showsVerticalScrollIndicator={false}
        >
            <InfoRow
                value={org.createdAt ? new Date(org.createdAt).toLocaleString("tr-TR") : "-"}
                label={localize("org-preview-created-at")}
            />
            <InfoRow
                value={org.authorizedPersonFullName || "-"}
                label={localize("org-preview-auth-name")}
            />
            <InfoRow
                label={localize("org-preview-auth-email")}
                value={org.authorizedPersonEmail || "-"}
            />
            <InfoRow
                label={localize("org-preview-auth-phone")}
                value={org.authorizedPersonPhone || "-"}
            />
            <InfoRow
                label={localize("org-preview-name")}
                value={org.name}
            />
            <InfoRow
                label={localize("org-preview-phone")}
                value={org.phone || "-"}
            />
            <InfoRow
                label={localize("org-preview-email")}
                value={org.email || "-"}
            />
            <InfoRow
                label={localize("org-preview-address")}
                value={org.address || "-"}
            />
        </ScrollView>
        <Button
            onPress={() => NCoreUIKitModal.close({
                id: modalId
            })}
            title={localize("org-preview-close")}
            spreadBehaviour="stretch"
            color="primary"
            type="neutral"
        />
    </View>;
};

export default OrganizationPreviewModal;
